
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import StartupPage from "./pages/StartupPage";
import RetailPage from "./pages/RetailPage";
import CreatorPage from "./pages/CreatorPage";

import FreelancerPage from "./pages/FreelancerPage";
import StudioPage from "./pages/StudioPage";
import AgentsPage from "./pages/AgentsPage";
import PlaybooksPage from "./pages/PlaybooksPage";
import VisualLabPage from "./pages/VisualLabPage";
import DailyCircusPage from "./pages/DailyCircusPage";
import AboutPage from "./pages/AboutPage";
import EnterprisePage from "./pages/EnterprisePage";
import ContactPage from "./pages/ContactPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SeoLandingPage from "./pages/SeoLandingPage";
import LaunchMarketingCampaignsPage from "./pages/LaunchMarketingCampaignsPage";
import GenerateBlogArticlesPage from "./pages/GenerateBlogArticlesPage";
import GettingStartedPage from "./pages/GettingStartedPage";
import AIAdGalleryPage from "./pages/AIAdGalleryPage";
import GimmefyGalleryPage from "./pages/GimmefyGalleryPage";
import BrandAnalyzerPage from "./pages/BrandAnalyzerPage";
import GetFreeBrandReportPage from "./pages/GetFreeBrandReportPage";
import PromptAndPlatePage from "./pages/PromptAndPlatePage";

import CustomerStoriesPage from "./pages/CustomerStoriesPage";
import SinglifeCaseStudyPage from "./pages/SinglifeCaseStudyPage";
import ComparisonPage from "./pages/ComparisonPage";

import TellAFriendPage from "./pages/TellAFriendPage";
import ROICalculatorPage from "./pages/ROICalculatorPage";
import ClientScripts from "./components/ClientScripts";
import CookieConsent from "./components/CookieConsent";
import MetaDetails from "./components/MetaDetails";
import { CookieConsentProvider } from '@/contexts/CookieConsentContext';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const queryClient = new QueryClient();

const RouteRecovery = () => {
  const location = useLocation();
  useEffect(() => {
    document.body.classList.remove('dialog-open');
    document.body.style.overflow = '';
  }, [location.pathname]);
  return null;
};

const App = () => {


  return (
    <CookieConsentProvider>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />

            <BrowserRouter>
              <MetaDetails />
              <RouteRecovery />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/enterprise" element={<EnterprisePage />} />
                <Route path="/roi-calculator" element={<ROICalculatorPage />} />
                <Route path="/contact" element={<ContactPage />} />

                {/* New Use Cases routes */}
                <Route path="/startups" element={<StartupPage />} />
                <Route path="/creators-and-influencers" element={<CreatorPage />} />
                <Route path="/retail" element={<RetailPage />} />
                <Route path="/freelancers" element={<FreelancerPage />} />

                {/* Legacy Use Cases routes - proper redirects to prevent 403/401 */}
                <Route path="/startup" element={<Navigate to="/startups" replace />} />
                <Route path="/creator" element={<Navigate to="/creators-and-influencers" replace />} />
                <Route path="/freelancer" element={<Navigate to="/freelancers" replace />} />

                <Route path="/studio" element={<StudioPage />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/playbooks" element={<PlaybooksPage />} />
                <Route path="/visual-lab" element={<VisualLabPage />} />
                <Route path="/daily-circus" element={<DailyCircusPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/create-social-content-calendars-with-ai" element={<SeoLandingPage />} />
                <Route path="/launch-marketing-campaigns-with-ai" element={<LaunchMarketingCampaignsPage />} />
                <Route path="/generate-blog-articles-with-ai" element={<GenerateBlogArticlesPage />} />
                <Route path="/getting-started" element={<GettingStartedPage />} />
                <Route path="/ai-ad-gallery" element={<AIAdGalleryPage />} />
                <Route path="/gimmefy-gallery" element={<GimmefyGalleryPage />} />
                <Route path="/360-brand-ai-deep-dive" element={<BrandAnalyzerPage />} />
                <Route path="/get-free-brand-report" element={<GetFreeBrandReportPage />} />
                <Route path="/prompt-and-plate" element={<PromptAndPlatePage />} />

                <Route path="/customer-stories" element={<CustomerStoriesPage />} />
                <Route path="/customer-stories/singlife" element={<SinglifeCaseStudyPage />} />
                <Route path="/comparison" element={<ComparisonPage />} />

                <Route path="/tellafriend" element={<TellAFriendPage />} />
                <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            <CookieConsent />
            <ClientScripts />
          </TooltipProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </CookieConsentProvider>
  );
};

export default App;
